<script>
import CardWrapper from '@/Shared/CardWrapper'
import CardLayout from '@/Shared/CardLayout'
import FlashMessages from '@/Shared/FlashMessages'
import FileStatusBadge from '@/Pages/EmployeesImport/components/FileStatusBadge'
import { formatDateToPeriod } from '@/utils/date'
import { truncateText } from '@/utils/string'

export default {
    metaInfo: {
        title: Lang.get('employerdashboard::pages/employees-import/index.title'),
    },

    components: {
        CardWrapper,
        CardLayout,
        FlashMessages,
        FileStatusBadge,
    },

    props: {
        employeeImports: Object,
        employeeImportUploadFeatureEnabled: Boolean,
        tab: String,
    },

    methods: {
        truncateText,

        formatDateToPeriod(dateTime) {
            return formatDateToPeriod(dateTime)
        },
    },
}
</script>

<template>
    <card-wrapper :title="Lang.get('employerdashboard::pages/employees-import/index.title') "
                  :subtitle="Lang.get('employerdashboard::pages/employees-import/index.subtitle')">
        <template v-slot:nav aria-label="breadcrumb">
            <ol class="breadcrumb font-poppins font-weight-light font-size-14 color-blue px-0 bg-transparent">
                <li class="breadcrumb-item color-blue" aria-current="page">
                    {{ Lang.get('employerdashboard::pages/employees-import/index.title') }}
                </li>
            </ol>
        </template>

        <div class="col-12 col-md-8">
            <flash-messages></flash-messages>
        </div>

        <card-layout card_padding="p-0 pb-4">
            <ul class="nav nav-tabs flex-nowrap px-3 my-3" id="invoiceTab" role="tablist">
                <li class="nav-item">
                    <inertia-link class="nav-link font-poppins font-size-14 color-blue"
                                  :class="{active: this.tab === 'all'}"
                                  id="all-tab"
                                  :href="route('nl.employerdashboard.employees-import.overview', $page.props.auth.user.selected_company.uuid)"
                                  role="tab"
                                  aria-controls="home"
                                  :aria-selected="{true: this.tab === 'all'}">
                        {{ Lang.get('employerdashboard::pages/employees-import/index.tabs.all') }}
                    </inertia-link>
                </li>
                <li class="nav-item">
                    <inertia-link class="nav-link font-poppins font-size-14 color-blue"
                                  :class="{active: this.tab === 'processed'}"
                                  id="processed-tab"
                                  data-toggle="tab"
                                  :href="route('nl.employerdashboard.employees-import.overview', {company_as_uuid: $page.props.auth.user.selected_company.uuid, tab: 'processed'})"
                                  role="tab"
                                  aria-controls="processed"
                                  :aria-selected="{true: this.tab === 'processed'}">
                        {{ Lang.get('employerdashboard::pages/employees-import/index.tabs.processed') }}
                    </inertia-link>
                </li>
                <li class="nav-item">
                    <inertia-link class="nav-link font-poppins font-size-14 color-blue"
                                  :class="{active: this.tab === 'failed'}"
                                  id="failed-tab"
                                  data-toggle="tab"
                                  :href="route('nl.employerdashboard.employees-import.overview', {company_as_uuid: $page.props.auth.user.selected_company.uuid, tab: 'failed'})"
                                  role="tab"
                                  aria-controls="failed"
                                  :aria-selected="{true: this.tab === 'failed'}">
                        {{ Lang.get('employerdashboard::pages/employees-import/index.tabs.failed') }}
                    </inertia-link>
                </li>
            </ul>

            <div class="tab-content">
                <div class="tab-pane fade show active" id="all" role="tabpanel" aria-labelledby="all-tab">
                    <div class="d-flex justify-content-end px-4">
                        <inertia-link
                            v-if="employeeImportUploadFeatureEnabled"
                            class="btn btn-primary btn-sm font-poppins font-weight-bold btn-orange border-radius-19 px-4 mt-3"
                            :href="route('nl.employerdashboard.employees-import.uploads.create',{ company_as_uuid: $page.props.auth.user.selected_company.uuid, })"
                        >
                            {{ Lang.get('employerdashboard::shared.spreadsheet_sync_status.import_button') }}
                        </inertia-link>
                    </div>

                    <template v-if="employeeImports.data.length > 0">
                        <div class="card border-0">
                            <div class="card-header px-4 py-3 bg-white cursor-pointer collapsed table-card">
                                <div class="row color-blue">
                                    <div class="col-2 col-lg-1">
                                        <strong>{{ Lang.get('employerdashboard::pages/employees-import/index.columnHeaders.period') }}</strong>
                                    </div>
                                    <div class="col-7 col-lg-8">
                                        <strong>{{ Lang.get('employerdashboard::pages/employees-import/index.columnHeaders.filename') }}</strong>
                                    </div>
                                    <div class="col-3 col-lg-2">
                                        <strong>{{ Lang.get('employerdashboard::pages/employees-import/index.columnHeaders.status') }}</strong>
                                    </div>
                                    <div class="sr-only col-3 col-lg-2">
                                        {{ Lang.get('employerdashboard::pages/employees-import/index.columnHeaders.options') }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card border-0" v-for="employeeImport in employeeImports.data" :key="employeeImport.uuid">
                            <div class="card-header px-4 py-3 bg-white collapsed table-card color-blue">
                                <div class="row">
                                    <div class="col-2 col-lg-1">
                                        {{ formatDateToPeriod(employeeImport.reference_date) }}
                                    </div>
                                    <div class="col-6 col-lg-7">
                                        <p class="mb-1">
                                            {{ truncateText(employeeImport.latest_employee_import_file?.original_filename, 80) }}
                                        </p>
                                    </div>
                                    <div class="col-4 col-lg-3">
                                        <file-status-badge
                                            :employee-import-file="employeeImport.latest_employee_import_file"
                                            class="mt-1 w-100"></file-status-badge>
                                    </div>
                                    <div class="col-3 col-lg-1">
                                        <inertia-link
                                            :href="route('nl.employerdashboard.employees-import.file.show', {
                                                company_as_uuid: $page.props.auth.user.selected_company.uuid,
                                                employeeImport: employeeImport.uuid,
                                                employeeImportFile: employeeImport.latest_employee_import_file.uuid,
                                            })"
                                            class="font-poppins font-size-14 color-blue"
                                        >
                                            {{ Lang.get('employerdashboard::pages/employees-import/index.view_details') }}
                                        </inertia-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <div class="col-12 px-4 py-3">
                            <p class="font-poppins font-weight-light font-size-18 color-blue">
                                {{ Lang.get('employerdashboard::pages/employees-import/index.noEmployeeImports') }}
                            </p>
                        </div>
                    </template>
                </div>
            </div>
        </card-layout>
    </card-wrapper>
</template>

<style lang="scss" scoped>
.table-card {
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
</style>
